'use client';

import { cx } from 'class-variance-authority';
import Link from 'next/link';
import { forwardRef } from 'react';
import { ImageApi_createURL } from '~/app/api/image/[...identifier]/shared';
import Avatar from '~/features/auth/user/Avatar';
import { useI18n } from '~/features/i18n';
import { Auth } from '~/modules/auth/client';

/** The account avatar the switcher hangs off. Kept in its own module so the lazy wrapper can render
 * it without pulling in the switcher panel (see `PortalSwitcher.lazy.tsx`). */
export const PortalSwitcher_Trigger = forwardRef<
    HTMLButtonElement,
    {
        user: NonNullable<ReturnType<typeof Auth.useSession>['user']>;
        isOpen?: boolean;
        /** Renders the avatar as a link instead of a button — used when there is nothing to switch
         * to and the avatar should behave like the plain account button it replaced. */
        href?: string;
        color?: React.ComponentProps<typeof Avatar>['color'];
        className?: string;
    } & Pick<
        React.DOMAttributes<HTMLElement>,
        'onClick' | 'onFocus' | 'onPointerEnter' | 'onPointerLeave'
    >
>(({ user, isOpen, href, color, className, ...buttonProps }, ref) => {
    const i18n = useI18n();

    const avatar = (
        <Avatar
            imageUrl={ImageApi_createURL({
                attachableId: user.id,
                attachableType: 'user/profile',
            })}
            label={user.name || user.firstName || user.email || ''}
            color={color ?? 'custom'}
        />
    );

    if (href) {
        return (
            <Link
                href={href}
                aria-label={i18n._('portal.switcher.trigger_hint')}
                data-testid="portal-switcher"
                className={cx('rounded-xl', className)}
                {...buttonProps}
            >
                {avatar}
            </Link>
        );
    }

    return (
        <button
            ref={ref}
            type="button"
            aria-expanded={isOpen}
            aria-label={i18n._('portal.switcher.trigger_hint')}
            data-testid="portal-switcher"
            className={cx('rounded-xl', className)}
            {...buttonProps}
        >
            {avatar}
        </button>
    );
});

PortalSwitcher_Trigger.displayName = 'PortalSwitcher_Trigger';
